import { Pipe, PipeTransform } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { from, merge, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay, startWith, switchMap } from 'rxjs/operators';

import { MSTranslationService } from '@core/services';

/**
 * Traduce al inglés un texto que viene de datos (en español) usando el
 * traductor de Azure, y se actualiza solo cuando cambia el idioma.
 *
 * Devuelve un Observable, así que se usa siempre con `async`:
 * `{{ item.title | translateValue | async }}`
 */
@Pipe({
  name: 'translateValue',
  pure: true, // mismo texto → mismo Observable; los cambios de idioma los emite el Observable
  standalone: false
})
export class MSTranslateValue implements PipeTransform {

  /** Traducciones ya resueltas, compartidas entre instancias para no repetir llamadas al API */
  private static cache: Map<string, Observable<string>> = new Map();

  constructor(private translate: TranslateService,
    private _msTranslateService: MSTranslationService) {
  }

  /**
   * Emite el texto en el idioma activo y vuelve a emitir cada vez que cambia.
   * @param {string} text Texto original, en español
   * @returns {Observable<string>} Devuelve el texto traducido según el idioma
   */
  transform(text: string): Observable<string> {
    if (!text || !text.length) {
      return of(text);
    }

    const lang$ = merge(this.translate.onLangChange, this.translate.onDefaultLangChange).pipe(
      map(event => event.lang),
      startWith(this.translate.currentLang),
      distinctUntilChanged()
    );

    // switchMap descarta una traducción en curso si el idioma vuelve a cambiar
    return lang$.pipe(
      switchMap(lang => lang === 'en' ? this.getTranslateValue(text) : of(text))
    );
  }

  /**
   * Traduce un valor de texto a inglés, reutilizando la traducción si ya se pidió.
   * Si el API falla se muestra el texto original.
   * @param {string} text Indica el texto a traducir
   * @returns {Observable<string>} Devuelve el texto en inglés
   */
  private getTranslateValue(text: string): Observable<string> {
    let translation$ = MSTranslateValue.cache.get(text);

    if (!translation$) {
      translation$ = from(this._msTranslateService.getTranslation(text)).pipe(
        map(value => value || text),
        catchError(() => {
          MSTranslateValue.cache.delete(text); // que se reintente la próxima vez
          return of(text);
        }),
        shareReplay(1)
      );

      MSTranslateValue.cache.set(text, translation$);
    }

    return translation$;
  }
}
